import type { Offer, Product, UnitPriceSpecification } from "@decocms/apps-commerce/types";

// Mesma regra usada pelo próprio framework em `toProductShelf` (função interna
// não exportada, node_modules/@decocms/apps-vtex/utils/transform.ts) — mantém
// só o essencial (preço, PIX, melhor parcela sem juros) e descarta o
// detalhamento completo de parcelamento por bandeira, que só é necessário na
// PDP/checkout. Numa PLP com ~48 produtos isso sozinho responde pela maior
// parte do peso do HTML (confirmado: ~174 entradas de parcelamento por
// produto, 8.369 no total numa página de categoria).
const SCHEMA_LIST_PRICE = "https://schema.org/ListPrice";
const SCHEMA_SALE_PRICE = "https://schema.org/SalePrice";
const SCHEMA_SRP = "https://schema.org/SRP";
const SCHEMA_INSTALLMENT = "https://schema.org/Installment";

/**
 * Bug real de produção (parcelamento sumindo/errado na vitrine): a regra
 * anterior comparava `billingIncrement * billingDuration` com o total da
 * parcela (`price`) e exigia que fechassem com 1 centavo de tolerância. Mas
 * a VTEX arredonda o valor de cada parcela pra baixo, então essa conta quase
 * nunca fecha: 386,10 em 4x vem como 4x R$ 96,52 = R$ 386,08 — sobram 2
 * centavos e a parcela era classificada como "com juros". Aí `leanOffer`
 * descartava a única parcela sem juros do produto e sobrava só o PIX (1x) na
 * oferta enxuta: o card mostrava "ou 1x de R$ 386,10" em vez de "ou 4x de
 * R$ 96,52" (e nada, quando o produto nem tinha PIX na lista). Só preços
 * divisíveis pelo número de parcelas (299 em 3x, 351,20 em 4x) passavam — daí
 * a mesma vitrine acertar alguns produtos e errar outros.
 *
 * Sem juros é, na verdade, "o total da parcela não passa do preço à vista"
 * (mesmo critério que `installmentToString` em `~/sdk/useOffer.ts` usa pra
 * escrever "sem juros"). Comparar com o preço da oferta também evita o falso
 * positivo do caminho oposto: 5x de R$ 80,72 = R$ 403,60 fecha exatamente com
 * o próprio total, mas esse total é maior que os R$ 386,10 à vista — é parcela
 * com juros.
 */
function isNoInterest(spec: UnitPriceSpecification, sellingPrice: number): boolean {
  if (spec.billingDuration == null || spec.billingIncrement == null || spec.price == null) {
    return false;
  }
  return spec.price <= sellingPrice + 0.01;
}

function leanOffer(offer: Offer): Offer {
  const leanSpecs: UnitPriceSpecification[] = [];
  let bestNoInterest: UnitPriceSpecification | null = null;

  for (const spec of offer.priceSpecification ?? []) {
    if (
      (spec.priceType === SCHEMA_LIST_PRICE ||
        spec.priceType === SCHEMA_SALE_PRICE ||
        spec.priceType === SCHEMA_SRP) &&
      spec.priceComponentType !== SCHEMA_INSTALLMENT
    ) {
      leanSpecs.push(spec);
      continue;
    }

    if (spec.priceComponentType === SCHEMA_INSTALLMENT && spec.name?.toUpperCase() === "PIX") {
      leanSpecs.push(spec);
      continue;
    }

    if (
      spec.priceComponentType === SCHEMA_INSTALLMENT &&
      isNoInterest(spec, offer.price) &&
      (bestNoInterest == null ||
        (spec.billingDuration ?? 0) > (bestNoInterest.billingDuration ?? 0))
    ) {
      bestNoInterest = spec;
    }
  }

  if (bestNoInterest) {
    leanSpecs.push(bestNoInterest);
  }

  return { ...offer, priceSpecification: leanSpecs };
}

function leanAggregateOffer<T extends Product["offers"]>(offers: T): T {
  if (!offers) return offers;
  return { ...offers, offers: offers.offers?.map(leanOffer) };
}

/**
 * Reduz o detalhamento de parcelamento em `product.offers` e em cada SKU de
 * `isVariantOf.hasVariant` pro mínimo necessário pra card/listagem. Mantém a
 * lista de variantes intacta (cor/tamanho continuam trocáveis no card).
 */
export function stripHeavyInstallments(product: Product): Product {
  return {
    ...product,
    offers: leanAggregateOffer(product.offers),
    isVariantOf: product.isVariantOf
      ? {
        ...product.isVariantOf,
        hasVariant: product.isVariantOf.hasVariant?.map((variant) => ({
          ...variant,
          offers: leanAggregateOffer(variant.offers),
        })),
      }
      : product.isVariantOf,
  };
}
